import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { roleService, Role } from '../services/roleService';
import AddEditRoleDialog from './AddEditRoleDialog';

interface RolesTableProps {
  externalAddTrigger?: number;
  hideAddButton?: boolean;
}

const RolesTable: React.FC<RolesTableProps> = ({ externalAddTrigger, hideAddButton = false }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  // Handle external add trigger
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0) {
      handleAddRole();
    }
  }, [externalAddTrigger]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await roleService.getRoles();
      setRoles(data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Hiba történt a szerepek betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = () => {
    setSelectedRole(null);
    setDialogOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setDialogOpen(true);
  };

  const handleDeleteClick = (role: Role) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRole) return;

    try {
      await roleService.deleteRole(selectedRole.id);
      setSuccessMessage('Szerepkör sikeresen törölve');
      fetchRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Hiba történt a szerepkör törlése során');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRole(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRole(null);
  };

  const handleDialogSuccess = () => {
    setSuccessMessage(
      selectedRole ? 'Szerepkör sikeresen frissítve' : 'Szerepkör sikeresen létrehozva'
    );
    fetchRoles();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Szerepek betöltése...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4" gutterBottom>
          Szerepek kezelése
        </Typography>
        {!hideAddButton && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRole}
            sx={{ backgroundColor: '#ff8c42', '&:hover': { backgroundColor: '#e67a35' } }}
          >
            Új szerepkör
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Név</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Jogosultságok száma</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  {role.name}
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${role.permissions?.length || 0} jogosultság`}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Tooltip title="Szerkesztés">
                    <IconButton
                      size="small"
                      onClick={() => handleEditRole(role)}
                      sx={{ color: '#ff8c42', mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Törlés">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(role)}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {roles.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Nincsenek szerepkörök
          </Typography>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <AddEditRoleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        role={selectedRole}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Szerepkör törlése</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Biztosan törölni szeretnéd a(z) <strong>{selectedRole?.name}</strong> szerepkört?
            Ez a művelet nem vonható vissza.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Mégse
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesTable;
